import { Dialog, Notify, Quasar } from 'quasar'
import { createPinia } from 'pinia'
import { Fragment, createApp, h } from 'vue'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { hydrationTrackerPlugin } from './modules/pinia'
import { createHead } from '@unhead/vue'
import * as Sentry from '@sentry/vue'
import BerkshireComponents from '@berkshireassociates/berkshirecomponents'
import router from './modules/router'
import { loadFonts } from './plugins/webfontloader'
import AppElement from './App.vue'
import { vReplace } from './utils/directives'
import type { App } from 'vue'
import { setupFocusMode } from '@berkshireassociates/berkshirecomponents'
import { VueQueryPlugin } from '@tanstack/vue-query'

import 'virtual:windi.css'
import './styles/main.css'

// Import Quasar css
import '@quasar/extras/roboto-font/roboto-font.css'
import '@quasar/extras/material-icons/material-icons.css'
import '@quasar/extras/material-icons-outlined/material-icons-outlined.css'
import 'quasar/src/css/index.sass'

function sentryStartup({ app, router }: { app: any; router: any }): void {
  // https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/#tracepropagationtargets
  // what transactions should be tracked?

  const tracePropagationTargets = [
    window.__env?.FE_HOSTNAME ?? '',
    ...(window.__env?.USE_LOCAL
      ? [window.__env?.LOCAL_API_HOSTNAME ?? '']
      : [window.__env?.API_HOSTNAME ?? '']),
    /^\//,
  ]

  const sampleRate = window.__env?.SAMPLE_RATE ?? 0

  Sentry.init({
    app,
    dsn: 'https://86c5df458f1dce9456dcc9d454e94140@o4506507853692928.ingest.sentry.io/4506508104302592',
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracePropagationTargets,
      }),
    ],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    // Vue logError calls are routed through sentry (and will not be seen in the dev console)
    // Set true to allow dev console error logging
    logErrors: true,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: sampleRate,
    // * other settings *
    environment: window.__env?.ENVIRONMENT_NAME,
    initialScope: {
      tags: {
        'device.screen_width_pixels': window.innerWidth,
        'device.screen_height_pixels': window.innerHeight,
      },
    },
  })
}

function setupApp(app: App<Element>) {
  const head = createHead()

  const runningUnitTests = import.meta.env.MODE === 'test'

  const promotionLevelIsProduction =
    window.__env?.PROMOTION_LEVEL === 'production'

  const runSentry = !runningUnitTests && promotionLevelIsProduction

  if (runSentry) {
    sentryStartup({ app, router })
  }

  app.directive('replace', vReplace)

  app.use(head)
  app.use(Quasar, {
    plugins: {
      Notify,
      Dialog,
    },
    config: {
      brand: {
        primary: 'var(--ba-primary)',
        secondary: 'var(--ba-secondary)',
      },
      notify: {
        /* look at QuasarConfOptions from the API card */
      },
    },
  })

  app.use(BerkshireComponents)

  app.use(pinia)
  app.use(VueQueryPlugin)

  // !! may user this later if we decide to go to a modular plugin system, will keep commented out for potential later use
  // Object.values(import.meta.globEager('./modules/*.ts')).map(i => app.use(i.install))

  app.use(router)

  setupFocusMode()

  // mock service workers run only in development
  // async function initMockServiceWorkers() {
  //   if (window.__env?.PROMOTION_LEVEL === 'development') {
  //     const { worker } = await import('./msw/browser')
  //     worker.start()
  //   }
  // }

  app.mount(appHTMLID)
  // initMockServiceWorkers()
}

loadFonts()

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(hydrationTrackerPlugin)

pinia.use(({ store }) => {
  store.$router = markRaw(router)
})

const appHTMLID = '#app'
let app: App<Element> | null = null

// * Shows accessibility errors in bottom right corner
// * To enable vue-axe, uncomment axe-core optimizeDeps option in vite.config.mts
if (
  window.__env?.PROMOTION_LEVEL === 'development' &&
  window.__env?.SHOW_ACCESSIBLE_ERRORS === true
) {
  // Import VueAxe and add to render step of app
  import('vue-axe').then((res) => {
    const VueAxe = res.default
    const VueAxePopup = res.VueAxePopup
    app = createApp({
      render: () =>
        h(Fragment, [h(AppElement as App<Element>), h(VueAxePopup)]),
    })
    app.use(VueAxe)

    // run setupApp
    setupApp(app)
  })
} else {
  // Don't import VueAxe for render step of app
  app = createApp(AppElement)

  // run setupApp
  setupApp(app)
}
